/* ============================================================================
   Rendered sheet previews — the first page of an uploaded drawing PDF (or an
   image sheet), rendered client-side to a JPEG data-URI. Each preview syncs
   to D1 as its own row (kind "sheet") like site photos, so the register's
   hover previews and the markup viewer show the real sheet everywhere.

   On this device they live in IndexedDB, not localStorage. A register of a
   few hundred sheets is several megabytes of previews; kept in localStorage
   that filled the 5MB the browser allows and every later write — the
   project itself, the sync outbox — failed silently. Memory is the working
   copy; IndexedDB is the cache; the sync stamps in localStorage say what
   this device already holds, so nothing is re-downloaded on start.
   ========================================================================== */
import { useEffect, useState } from "react";

export interface SheetImage { id: string; dataUri: string }

const LEGACY_KEY = "atelier.sheets.v1";
const IDB_NAME = "atelier-sheets";
const IDB_STORE = "sheets";

type State = { sheets: SheetImage[] };
let state: State = { sheets: [] };
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());

/* ------------------------------------------------------------- IndexedDB */
/* One connection for the life of the page — every call used to open another,
   and a report page with dozens of photos held dozens open. */
let idbCached: Promise<IDBDatabase | null> | null = null;
function idb(): Promise<IDBDatabase | null> {
  if (!idbCached) idbCached = openIdb().then((db) => {
    if (!db) idbCached = null;
    else { db.onversionchange = () => { db.close(); idbCached = null; }; db.onclose = () => { idbCached = null; }; }
    return db;
  });
  return idbCached;
}
function openIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") { resolve(null); return; }
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE, { keyPath: "id" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
}
async function idbAll(): Promise<SheetImage[]> {
  const db = await idb();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).getAll();
      req.onsuccess = () => resolve((req.result as SheetImage[]) ?? []);
      req.onerror = () => resolve([]);
    } catch { resolve([]); }
  });
}
async function idbPut(rows: SheetImage[]): Promise<void> {
  if (!rows.length) return;
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      const st = tx.objectStore(IDB_STORE);
      for (const r of rows) st.put(r);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}
async function idbDelete(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      const st = tx.objectStore(IDB_STORE);
      for (const id of ids) st.delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}

/* -------------------------------------------------------------- startup */
/** Resolves once the device's cached previews are in memory. Sync waits on
    it before deciding what is missing. */
export const sheetsReady: Promise<void> = (async () => {
  // one-time move out of localStorage — this is what frees the quota
  let legacy: SheetImage[] = [];
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw) { legacy = (JSON.parse(raw) as State).sheets ?? []; }
  } catch { /* unreadable — nothing to carry over */ }
  const cached = await idbAll();
  const map = new Map<string, SheetImage>();
  for (const s of legacy) map.set(s.id, s);
  for (const s of cached) map.set(s.id, s);
  // whatever arrived in memory before the cache loaded wins — it is newer
  for (const s of state.sheets) map.set(s.id, s);
  state = { sheets: [...map.values()] };
  if (legacy.length) await idbPut(legacy);
  try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
  notify();
})();

/* ------------------------------------------------------------- readers */
export function useSheetImage(id: string | undefined): string | undefined {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return id ? state.sheets.find((s) => s.id === id)?.dataUri : undefined;
}
/** Non-hook read — one-off consumers (PDF export, revision diffs). */
export function sheetImage(id: string | undefined): string | undefined {
  return id ? state.sheets.find((s) => s.id === id)?.dataUri : undefined;
}

/* --- sync hooks --------------------------------------------------------- */
let sheetListener: ((s: SheetImage) => void) | null = null;
export function onSheetChange(f: (s: SheetImage) => void) { sheetListener = f; }
export function getSheetAggregates(): SheetImage[] { return state.sheets; }
export function replaceSheetAggregates(sheets: SheetImage[]) {
  const map = new Map(state.sheets.map((s) => [s.id, s]));
  for (const s of sheets) map.set(s.id, s);
  state = { sheets: [...map.values()] };
  void idbPut(sheets);
  notify();
}
/** Rows deleted elsewhere — drop the cached preview too. */
export function dropSheetsLocally(ids: string[]) {
  const gone = new Set(ids);
  state = { sheets: state.sheets.filter((s) => !gone.has(s.id)) };
  void idbDelete(ids);
  notify();
}

/* --- actions -------------------------------------------------------------- */
export const sheetActions = {
  put(s: SheetImage) {
    state = { sheets: [s, ...state.sheets.filter((x) => x.id !== s.id)] };
    void idbPut([s]);
    notify();
    sheetListener?.(s);
  },
};
